import { ISSUE, ISSUE_TARGET, configIssue } from '../catalog/issue';
import type { ConfigIssue } from '../catalog/issue';
import { RCU_LIMITS } from '../catalog/limits';
import { RAIL_TYPE_SPECS } from '../catalog/railType';
import type { RailTypeKey } from '../catalog/railType';
import type { RailId } from '../ids';
import { newRailId } from '../ids';
import type { CommandResult, DinRail, RcuConfig } from '../model';
import { moduleCount } from '../query/capacity';
import { findRail, loadsOnRail, railsOfType } from '../query/lookup';
import { fail } from '../result';
import { changedBy } from './outcome';

export interface RailDraft {
  readonly type: RailTypeKey;
  readonly address: number;
  readonly label: string;
}

/**
 * A rail's type is fixed at creation — changing it would invalidate every load
 * wired to it, so the technician deletes and re-adds instead.
 */
export interface RailPatch {
  readonly address?: number;
  readonly label?: string;
}

const railNotFound = (id: RailId): ConfigIssue =>
  configIssue(ISSUE.RAIL_NOT_FOUND, { kind: ISSUE_TARGET.RAIL, id }, id);

/**
 * Problems with a rail's type and address, ignoring `ignoreId` so a rail being
 * edited does not conflict with itself.
 */
const railShapeIssue = (
  config: RcuConfig,
  type: RailTypeKey,
  address: number,
  ignoreId: RailId | null,
): ConfigIssue | null => {
  const spec = RAIL_TYPE_SPECS[type];
  const siblings = railsOfType(config, type).filter((rail) => rail.id !== ignoreId);

  if (siblings.length >= RCU_LIMITS.MAX_RAILS_PER_TYPE) {
    return configIssue(ISSUE.RAIL_TYPE_LIMIT, { kind: ISSUE_TARGET.RAIL }, spec.shortLabel);
  }
  if (address < RCU_LIMITS.MIN_RAIL_ADDRESS || address > RCU_LIMITS.MAX_RAIL_ADDRESS) {
    return configIssue(
      ISSUE.RAIL_ADDRESS_OUT_OF_RANGE,
      { kind: ISSUE_TARGET.RAIL },
      String(address),
    );
  }
  if (siblings.some((rail) => rail.address === address)) {
    return configIssue(
      ISSUE.RAIL_ADDRESS_CONFLICT,
      { kind: ISSUE_TARGET.RAIL },
      `${spec.shortLabel} address ${address}`,
    );
  }
  return null;
};

export const addRail = (
  config: RcuConfig,
  draft: RailDraft,
  now: string,
): CommandResult<RailId> => {
  /** Rails and DALI bridges share one module budget on the device. */
  if (moduleCount(config) >= RCU_LIMITS.MAX_MODULES_TOTAL) {
    return fail(
      configIssue(ISSUE.MODULE_TOTAL_LIMIT, { kind: ISSUE_TARGET.RAIL }, String(moduleCount(config))),
    );
  }

  const shapeIssue = railShapeIssue(config, draft.type, draft.address, null);
  if (shapeIssue !== null) return fail(shapeIssue);

  const rail: DinRail = {
    id: newRailId(),
    type: draft.type,
    address: draft.address,
    label: draft.label,
  };
  return changedBy({ ...config, rails: [...config.rails, rail] }, rail.id, now);
};

export const updateRail = (
  config: RcuConfig,
  id: RailId,
  patch: RailPatch,
  now: string,
): CommandResult<RailId> => {
  const rail = findRail(config, id);
  if (rail === undefined) return fail(railNotFound(id));

  const next: DinRail = { ...rail, ...patch };
  const shapeIssue = railShapeIssue(config, next.type, next.address, id);
  if (shapeIssue !== null) return fail(shapeIssue);

  return changedBy(
    { ...config, rails: config.rails.map((current) => (current.id === id ? next : current)) },
    id,
    now,
  );
};

/**
 * Removes a rail and unwires every load on it. The loads survive as unwired
 * entries, so nothing the technician named disappears silently.
 */
export const removeRail = (
  config: RcuConfig,
  id: RailId,
  now: string,
): CommandResult<number> => {
  const rail = findRail(config, id);
  if (rail === undefined) return fail(railNotFound(id));

  const orphaned = new Set<string>(loadsOnRail(config, id).map((load) => load.id));

  return changedBy(
    {
      ...config,
      rails: config.rails.filter((current) => current.id !== id),
      loads: config.loads.map((load) =>
        orphaned.has(load.id) ? { ...load, channel: null } : load,
      ),
    },
    orphaned.size,
    now,
  );
};
